import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Eye, FileCheck2, AlertTriangle } from "lucide-react";

/* Security & AI Transparency statement.
 *
 * NSWRA / NSW Government compliance context: this page is the assessor-
 * and stakeholder-facing record of how RAAgent handles assessment data and
 * how the embedded AI is used. The wording is intentionally plain English
 * — the goal is for a Programme Director, SES Officer, or NSWRA reviewer
 * to read it and be left in no doubt about three things:
 *   1) Data residency, access control, and audit trail.
 *   2) That the AI never decides outcomes — it only extracts, classifies
 *      and surfaces options. Every assessment outcome is the SQP's call.
 *   3) That the AI cannot reach external systems, bypass auth, or modify
 *      assessment artefacts beyond what the SQP explicitly directs.
 */
export function SecurityStatement() {
  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="security-statement-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700 mb-2 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5"/> NSW Government — Security &amp; AI Transparency
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">How RAAgent handles your data and uses AI</h1>
        <p className="text-base text-slate-600 mt-2 leading-relaxed">
          RAAgent is built for the NSW Reconstruction Authority assessor workflow under the
          Disaster Recovery Funding Arrangements (DRFA). This page is the public record of
          how the platform handles council and claimant data and exactly where — and how —
          AI is used.
        </p>
      </div>

      {/* ============== Headline guarantees ============== */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="security-guarantees">
        <div className="card p-5">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <Lock className="w-5 h-5"/>
          </div>
          <h3 className="font-heading text-base font-semibold mb-1">Government-grade information security</h3>
          <p className="text-[12.5px] text-slate-600 leading-relaxed">
            All assessment data is stored in a private, access-controlled database. Sign-in is
            domain-locked to authorised RHE Project Consulting staff and explicitly named
            NSWRA stakeholders. Anyone else is rejected at the door with HTTP 403.
          </p>
        </div>
        <div className="card p-5">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <Eye className="w-5 h-5"/>
          </div>
          <h3 className="font-heading text-base font-semibold mb-1">AI cannot bypass security</h3>
          <p className="text-[12.5px] text-slate-600 leading-relaxed">
            The AI used by RAAgent has no access to your authentication, no ability to read
            other assessors&apos; jobs, and no path to any external system. Its access is
            scoped to one specific task — extracting numbers from a single PDF when the
            assessor clicks the button.
          </p>
        </div>
        <div className="card p-5">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <FileCheck2 className="w-5 h-5"/>
          </div>
          <h3 className="font-heading text-base font-semibold mb-1">The SQP makes every decision</h3>
          <p className="text-[12.5px] text-slate-600 leading-relaxed">
            RAAgent&apos;s tools surface options (Accept · RFI · Reject). The AI never marks an
            outcome on its own. The Suitably Qualified Professional clicks the verdict, and
            their click is what is written to the assessment artefact — never the AI&apos;s
            extraction.
          </p>
        </div>
      </div>

      {/* ============== Where the data lives ============== */}
      <div className="card p-6" data-testid="security-data-handling">
        <h2 className="font-heading text-2xl font-semibold mb-3">Where your data lives, and who can see it</h2>
        <ul className="space-y-2.5 text-[13.5px] text-slate-700 leading-relaxed list-disc pl-5">
          <li>
            <b>Storage.</b> Council submissions, assessor decisions, ledger workings, photo
            metadata, CET workings and verified-package snapshots are stored in a private
            MongoDB cluster scoped to the RAAgent application. There is no public read
            endpoint. Connection strings live in server-side environment variables, never
            in the frontend bundle.
          </li>
          <li>
            <b>Access control.</b> Sign-in requires an authorised email address. The list
            of approved addresses lives server-side (`ALLOWED_EMAIL_DOMAINS`,
            `ALLOWED_EMAIL_ADDRESSES`). Any other email returns 403 before the assessment
            UI is even loaded.
          </li>
          <li>
            <b>Audit trail.</b> Every assessor decision (per-DM verdict, per-line ledger
            override, scope confirmation, finalisation marker) is timestamped against the
            signed-in assessor&apos;s email. SLA timers (claim, in progress, completed) are
            persisted for every job. Nothing is silently changed in the background.
          </li>
          <li>
            <b>No third-party analytics or tracking.</b> RAAgent does not embed third-party
            analytics, marketing pixels or behavioural trackers. The only outbound calls
            from the backend are to (a) the assessor&apos;s database and (b) the AI provider
            for explicit, user-triggered PDF extraction.
          </li>
          <li>
            <b>File retention.</b> Uploaded ZIPs are processed into a private working
            directory. The verified-package layout is cached to the database so the
            preview survives disk pruning, but the raw uploads are not pushed to any
            external bucket.
          </li>
        </ul>
      </div>

      {/* ============== The trajectory — from assistant to SQP ============== */}
      <div className="card p-6 border-blue-200 bg-blue-50/30" data-testid="security-trajectory-section">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-blue-700 mb-2">Where this is heading</div>
        <h2 className="font-heading text-2xl font-semibold mb-3">The trajectory — from assistant to SQP</h2>
        <p className="text-[13.5px] text-slate-700 leading-relaxed">
          RAAgent today is an assistant. The DRFA Agent extracts, classifies, surfaces
          options and cites precedents &mdash; the SQP makes every decision and signs
          every assessment. That is the contract.
        </p>
        <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">
          The DRFA Agent is also the <b>endgame</b>. Every decision an RHE assessor makes
          today feeds the precedent corpus. Every clarification, override, and free-text
          rationale becomes training signal &mdash; not for the model weights (we don&apos;t
          fine-tune), but for the retrieval-and-reasoning layer that wraps the model.
          As the corpus matures, the Agent will progressively assume more of the SQP role:
          first as a strong recommender (today), then as a default-decider on
          high-confidence, low-risk lines with the SQP signing the bundle, and eventually
          as the SQP itself for the categories where it has demonstrably outperformed the
          human baseline. The DRFA Agent will become the SQP &mdash; it will eventually
          feed every assessment process end-to-end, with the human in escalation /
          oversight rather than line-by-line decision-making.
        </p>
        <div className="mt-4 grid md:grid-cols-3 gap-3 text-[12px]">
          <div className="rounded-md border border-blue-200 bg-white p-3" data-testid="trajectory-phase-now">
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-blue-700 mb-1">Today &mdash; Phase 1</div>
            <div className="font-semibold text-slate-900">DRFA Agent assists</div>
            <p className="text-slate-700 mt-1">AI extracts and classifies. Surfaces precedents and decision options. <b>SQP decides every line.</b> Audit trail on every click.</p>
          </div>
          <div className="rounded-md border border-blue-200 bg-white p-3" data-testid="trajectory-phase-next">
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-blue-700 mb-1">Next &mdash; Phase 2</div>
            <div className="font-semibold text-slate-900">DRFA Agent defaults</div>
            <p className="text-slate-700 mt-1">On high-confidence, low-risk lines the Agent picks a default verdict, the SQP confirms-or-overrides in a single click. SQP still signs the bundle.</p>
          </div>
          <div className="rounded-md border border-blue-200 bg-white p-3" data-testid="trajectory-phase-end">
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-blue-700 mb-1">Endgame &mdash; Phase 3</div>
            <div className="font-semibold text-slate-900">DRFA Agent is the SQP</div>
            <p className="text-slate-700 mt-1">For categories where the Agent demonstrably out-performs the human baseline against the precedent corpus, the Agent <b>is</b> the SQP. Humans handle escalations and audits.</p>
          </div>
        </div>
        <p className="text-[12px] text-slate-600 leading-relaxed mt-4 italic">
          Each phase shift is a governed milestone &mdash; not a silent upgrade. The phase the
          DRFA Agent is operating under is always visible at the point of decision, the
          assessor can always override and audit, and a phase change requires an explicit
          governance decision (Programme Director sign-off, NSWRA notification, statement
          update on this page). No phase change happens because of how this page is worded.
        </p>
      </div>

      {/* ============== Exactly how AI is used ============== */}
      <div className="card p-6 border-emerald-200 bg-emerald-50/30" data-testid="security-ai-section">
        <h2 className="font-heading text-2xl font-semibold mb-1">Exactly how — and where — AI is used</h2>
        <p className="text-[13px] text-slate-600 italic mb-4">No mystery, no marketing waffle.</p>

        <div className="space-y-4 text-[13.5px] text-slate-700 leading-relaxed">
          <div>
            <h3 className="font-semibold mb-1">1. Where AI is used</h3>
            <p>
              AI is used in <b>one place only</b>: the &quot;Extract defects from PDF&quot; helper on
              the IngestJob page, which reads a SmartyGrants Form 306 claim form when the
              council uploads a PDF instead of the spreadsheet. The assessor explicitly
              clicks the button. Without that click, the AI is not invoked.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-1">2. What the AI is allowed to do</h3>
            <p>
              The AI reads the one PDF the assessor uploaded and returns a structured list
              of candidate defects: damage ID, description, scope, ex-GST cost, completion
              date. The output lands in a <b>review queue for the assessor</b>. Nothing is
              persisted as an assessment outcome at this step.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-1">3. What the AI is <em>not</em> allowed to do</h3>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>It does not access the assessor&apos;s database directly.</li>
              <li>It does not read other jobs, other councils, or anything outside the one PDF.</li>
              <li>It does not make any assessment decision (Accept / RFI / Reject) — those buttons exist for the SQP and only the SQP.</li>
              <li>It does not edit, sign or finalise any output artefact (the Assessment Spreadsheet, the Form 2, the Verified Package ZIP). Those are written by the platform using the SQP&apos;s recorded clicks.</li>
              <li>It cannot reach out to SmartyGrants, OneGMS, council systems, or any state-government system on its own.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-1">4. Provider, model and key handling</h3>
            <p>
              Defect extraction uses Anthropic Claude (Sonnet 4.5) via a server-side LLM
              gateway. The API key is held in a server-side environment variable and is
              <b> never shipped to the browser</b>. The prompt and the PDF go from the
              RAAgent backend → the gateway → the model, and the structured response
              comes back the same way. The model is invoked statelessly — there is no
              training, no retention beyond the gateway provider&apos;s standard terms,
              and no data shared between assessors.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-1">5. SQP authority is absolute</h3>
            <p>
              Per the NSWRA DRFA assessor framework, the Suitably Qualified Professional
              (SQP) is the decision-maker. RAAgent encodes that hierarchy directly: AI is
              a research assistant, the tools (Ledger Checker · Photo Checker · CET
              Verifier · DRFA Expert) are advisors, and the SQP&apos;s click is the only
              thing that produces an assessment outcome. If the SQP overrides every AI
              suggestion, the assessment is the override.
            </p>
          </div>
        </div>
      </div>

      {/* ============== Things to know ============== */}
      <div className="card p-6 border-amber-200 bg-amber-50/30" data-testid="security-disclosures">
        <h2 className="font-heading text-xl font-semibold mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600"/>
          Honest disclosures
        </h2>
        <ul className="list-disc pl-5 space-y-1.5 text-[13px] text-slate-700 leading-relaxed">
          <li>
            <b>Cloud hosting.</b> RAAgent runs on a managed cloud platform — not on
            on-premise government infrastructure. If your programme requires data
            residency inside a specific cloud region or government cloud, contact us
            before onboarding so we can scope the migration. The current preview
            environment is hosted in a region selected by the platform; production data
            handling is identical.
          </li>
          <li>
            <b>AI provider.</b> Anthropic Claude is the model used for PDF defect
            extraction. The model provider&apos;s standard terms apply for that single
            transaction. RAAgent does not opt the model into training on your data, and
            the prompt sent never includes other assessors&apos; jobs.
          </li>
          <li>
            <b>Authentication.</b> The current MVP uses an email allowlist (RHE domain
            + explicit guest list). NSW Gov SSO is on the roadmap (P2) and will replace
            the allowlist when your organisation is ready.
          </li>
          <li>
            <b>No silent learning &mdash; precedents are explicit and cited.</b> RAAgent
            captures every assessor decision into a queryable corpus (the &quot;precedent
            corpus&quot;, viewable in-app at {" "}
            <Link to="/precedents" className="text-blue-600 hover:underline">/precedents</Link>).
            When the DRFA Expert is asked a question, matching precedents from this corpus
            are retrieved and inserted into the prompt with explicit citations (
            <span className="font-mono">[Precedent 1]</span>, <span className="font-mono">[Precedent 2]</span>&hellip;)
            so the assessor knows exactly which prior decisions influenced the answer.
            The model is <b>not fine-tuned</b> on this data and the precedents are RHE-internal
            (institutional memory) &mdash; not shared with the AI provider for training, not
            shared across councils, not used to make decisions automatically. The SQP still
            decides.
          </li>
        </ul>
      </div>

      <div className="text-[11.5px] text-slate-500 leading-relaxed pt-2 border-t border-slate-200">
        Operated by RHE Project Consulting Pty Ltd for NSWRA assessor work under the
        Commonwealth/State DRFA. Questions or compliance reviews:{" "}
        <a href="mailto:hello@rheprojectconsulting.com.au" className="text-blue-600 hover:underline">hello@rheprojectconsulting.com.au</a>.
        For the assessor workflow, see the{" "}
        <Link to="/workflow/epar-scope" className="text-blue-600 hover:underline">workflow walkthroughs</Link>.
      </div>
    </div>
  );
}
